import { CalendarType } from '@xeetr/types/enums'

/**
 * Centralized query key factory for React Query
 *
 * Benefits:
 * - Type-safe query keys
 * - Consistent key structure across the app
 * - Easy to invalidate related queries
 * - Hierarchical structure for granular cache control
 *
 * Usage:
 * ```typescript
 * useQuery({
 *   queryKey: queryKeys.bookings.list(tenantId, { state, from, until }),
 *   queryFn: () => fetchBookings(...)
 * })
 *
 * // Invalidate all bookings for a tenant
 * queryClient.invalidateQueries({
 *   queryKey: queryKeys.bookings.all(tenantId)
 * })
 * ```
 */
export const queryKeys = {
  tenant: (tenantId: string) => ['tenant', tenantId] as const,
  user: () => ['user'] as const,
  userSettings: (userId: string) => ['userSettings', userId] as const,
  bookings: {
    all: (tenantId: string) => ['bookings', tenantId] as const,
    list: (
      tenantId: string,
      filters: {
        state: string
        from: Date
        until: Date | null
      }
    ) => [...queryKeys.bookings.all(tenantId), 'list', filters] as const,
    detail: (tenantId: string, bookingId: string) =>
      [...queryKeys.bookings.all(tenantId), 'detail', bookingId] as const,
    export: (tenantId: string, filters: { from: Date; until: Date }) =>
      [...queryKeys.bookings.all(tenantId), 'export', filters] as const,
  },

  calendar: {
    all: (tenantId: string) => ['calendar', tenantId] as const,
    events: (tenantId: string, date: Date, type: CalendarType) =>
      [...queryKeys.calendar.all(tenantId), 'events', { date: date.toISOString(), type }] as const,
    resources: (tenantId: string) => [...queryKeys.calendar.all(tenantId), 'resources'] as const,
  },

  settings: (tenantId: string) => ['settings', tenantId] as const,

  employees: {
    all: (tenantId: string) => ['employees', tenantId] as const,
    absence: (tenantId: string, employeeId: string) =>
      [...queryKeys.employees.all(tenantId), 'absence', employeeId] as const,
  },

  rooms: {
    all: (tenantId: string) => ['rooms', tenantId] as const,
  },

  services: {
    all: (tenantId: string) => ['services', tenantId] as const,
  },

  shifts: {
    all: (tenantId: string) => ['shifts', tenantId] as const,
  },

  foodEvents: {
    all: (tenantId: string) => ['foodEvents', tenantId] as const,
    detail: (tenantId: string, eventId: string) =>
      [...queryKeys.foodEvents.all(tenantId), 'detail', eventId] as const,
  },

  tableUtilization: (tenantId: string) => ['tableUtilization', tenantId] as const,

  changelog: (bookingId: string) => ['changelog', bookingId] as const,

  customers: {
    all: (tenantId: string) => ['customers', tenantId] as const,
    filter: (
      tenantId: string,
      query: string,
      page?: number,
      limit?: number,
      sortBy?: string,
      sortOrder?: 'asc' | 'desc'
    ) =>
      [
        ...queryKeys.customers.all(tenantId),
        'filter',
        query,
        page,
        limit,
        sortBy,
        sortOrder,
      ] as const,
    detail: (tenantId: string, customerId: string) =>
      [...queryKeys.customers.all(tenantId), 'detail', customerId] as const,
    bookings: (tenantId: string, customerId: string) =>
      [...queryKeys.customers.all(tenantId), 'bookings', customerId] as const,
  },

  apiKeys: (tenantId: string) => ['apiKeys', tenantId] as const,

  mailConfigStatus: (tenantId: string) => ['mailConfigStatus', tenantId] as const,

  dailyNotes: {
    all: (tenantId: string) => ['dailyNotes', tenantId] as const,
    list: (tenantId: string, from?: string, until?: string) =>
      [...queryKeys.dailyNotes.all(tenantId), 'list', { from, until }] as const,
  },

  monthlyOverview: {
    all: (tenantId: string) => ['monthlyOverview', tenantId] as const,
    bookings: (tenantId: string, monthStart: Date, monthEnd: Date) =>
      [
        ...queryKeys.monthlyOverview.all(tenantId),
        'bookings',
        monthStart.toISOString(),
        monthEnd.toISOString(),
      ] as const,
    dailyNotes: (tenantId: string, monthStart: Date, monthEnd: Date) =>
      [
        ...queryKeys.monthlyOverview.all(tenantId),
        'dailyNotes',
        monthStart.toISOString(),
        monthEnd.toISOString(),
      ] as const,
    events: (tenantId: string, monthStart: Date, monthEnd: Date) =>
      [
        ...queryKeys.monthlyOverview.all(tenantId),
        'events',
        monthStart.toISOString(),
        monthEnd.toISOString(),
      ] as const,
  },
}
